import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { instance } from "@/lib/hooks";
import { FeatureFlag } from "@/types";

export function useFilterOptions() {
  // Fetch the feature flags to extract unique orgs/groups
  const { data: flags } = useQuery({
    queryKey: ["cmw-feature-flags-filter-options"],
    queryFn: async () => {
      const response = await instance.get<FeatureFlag[]>("/cmw-feature-flags");
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const organizationOptions = useMemo(() => {
    if (!flags) {
      return [];
    }

    const organizationMap = new Map<string, { value: string; label: string }>();

    flags.forEach((flag: FeatureFlag) => {
      flag.gates.forEach((gate) => {
        if (
          typeof gate.for === "object" &&
          gate.for.entity === "organization" &&
          gate.enabled
        ) {
          organizationMap.set(String(gate.for.entity_id), {
            value: String(gate.for.entity_id),
            label: gate.for.label || "",
          });
        }
      });
    });

    return Array.from(organizationMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [flags]);

  const groupOptions = useMemo(() => {
    if (!flags) {
      return [];
    }

    const groupMap = new Map<string, { value: string; label: string }>();

    flags.forEach((flag: FeatureFlag) => {
      flag.gates.forEach((gate) => {
        if (
          typeof gate.for === "object" &&
          gate.for.entity === "group" &&
          gate.enabled
        ) {
          groupMap.set(String(gate.for.entity_id), {
            value: String(gate.for.entity_id),
            label: gate.for.label || "",
          });
        }
      });
    });

    return Array.from(groupMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [flags]);

  return {
    organizationOptions,
    groupOptions,
  };
}
